import { useEffect, useState, useCallback, useRef } from 'react';
import { onSnapshot, query, where, orderBy, limit, collection, getDocs } from 'firebase/firestore';
import { db, getOrCreateConversation, listUserConversations, listConversationMessages, sendDirectMessage } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { DirectConversation, DirectMessage } from '../../types/messaging';

interface Props { onClose?: () => void; startWithUserId?: string; conversationId?: string; }

// Basit kayan panel
export function MessagingPanel({ onClose, startWithUserId, conversationId }: Props){
  const { isLoggedIn, userProfile } = useAuth();
  const [conversations, setConversations] = useState<DirectConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [msgInput, setMsgInput] = useState('');
  const [loadingConv, setLoadingConv] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [convError, setConvError] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);

  // Detect mobile viewport
  useEffect(()=>{
    const mq = window.matchMedia('(max-width: 680px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return ()=> mq.removeEventListener('change', apply);
  }, []);

  const loadConversations = useCallback(async ()=>{
    if(!isLoggedIn) return; setLoadingConv(true); setConvError(null);
    try {
      const list = await listUserConversations(30); setConversations(list);
    } catch(e:any){
      console.warn('Konuşmalar yüklenirken hata (liste):', e);
      setConvError(e?.message || 'Konuşmalar yüklenemedi');
    } finally { setLoadingConv(false); }
  },[isLoggedIn]);

  const openConversation = useCallback(async (convId: string) => {
    setActiveId(convId); setLoadingMsgs(true); setMsgError(null);
    try {
      const res = await listConversationMessages(convId, { pageSize: 50 }); setMessages(res.items);
    } catch(e:any){
      console.warn('Mesajlar ilk yüklemede hata:', e);
      setMsgError(e?.message || 'Mesajlar yüklenemedi');
    } finally { setLoadingMsgs(false); setTimeout(()=> bottomRef.current?.scrollIntoView({behavior:'smooth'}),100); }
  },[]);

  const startDirectWith = useCallback(async (otherId: string) => {
    const id = await getOrCreateConversation(otherId); await loadConversations(); await openConversation(id);
  },[loadConversations, openConversation]);

  const handleSend = async () => {
    if(!activeId) return; const text = msgInput.trim(); if(!text) return;
    setMsgInput(''); // optimistic append
    const optimistic: DirectMessage = { id: 'tmp-'+Date.now(), conversationId: activeId, senderId: userProfile!.uid, content: text, createdAt: new Date().toISOString() };
    setMessages(m=> [...m, optimistic]);
    setTimeout(()=> bottomRef.current?.scrollIntoView({behavior:'smooth'}), 10);
    try { await sendDirectMessage(activeId, text); } catch { /* optionally show error */ }
  };

  // Real-time conversation list listener
  useEffect(()=>{
    if(!isLoggedIn) return; let unsub: (()=>void)|null = null; let fallbackTried = false;
    setConvError(null);
    try {
      const qConv = query(collection(db,'dm_conversations'), where('participants','array-contains', userProfile!.uid), orderBy('updatedAt','desc'), limit(30));
      unsub = onSnapshot(qConv, snap => {
        setConversations(snap.docs.map(d=> ({ id:d.id, ...(d.data() as any) })) as DirectConversation[]);
      }, async (err)=>{
        console.warn('Realtime konuşma listener hata:', err);
        if(!fallbackTried){
          fallbackTried = true;
          // Index yoksa fallback: orderBy'sız listener ve client-side sort
            try {
              const simpleQ = query(collection(db,'dm_conversations'), where('participants','array-contains', userProfile!.uid), limit(30));
              unsub = onSnapshot(simpleQ, snap2 => {
                const list = snap2.docs.map(d=> ({ id:d.id, ...(d.data() as any) })) as DirectConversation[];
                list.sort((a,b)=> (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || ''));
                setConversations(list);
              });
              // İlk yükleme manuel
              const first = await getDocs(simpleQ);
              const listFirst = first.docs.map(d=> ({ id:d.id, ...(d.data() as any) })) as DirectConversation[];
              listFirst.sort((a,b)=> (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || ''));
              setConversations(listFirst);
              setConvError('Performans için Firestore index oluşturun: dm_conversations participants array-contains + updatedAt desc');
            } catch(e2:any){
              setConvError('Konuşmalar yüklenemedi: '+ (e2?.message || 'bilinmeyen hata'));
            }
        } else {
          setConvError('Konuşmalar yüklenemedi');
        }
      });
    } catch(e:any){
      console.warn('Konuşma listener başlatılamadı:', e);
      setConvError(e?.message || 'Konuşmalar yüklenemedi');
    }
    return ()=> { if(unsub) unsub(); };
  },[isLoggedIn, userProfile]);

  // Real-time messages for active conversation
  useEffect(()=>{
    if(!activeId) return; let unsub: (()=>void)|null = null; let fallback = false; setMsgError(null);
    try {
      const qMsgs = query(collection(db,'dm_messages'), where('conversationId','==', activeId), orderBy('createdAt','asc'), limit(200));
      unsub = onSnapshot(qMsgs, snap => {
        setMessages(snap.docs.map(d=> ({ id:d.id, ...(d.data() as any) })) as DirectMessage[]);
        setTimeout(()=> bottomRef.current?.scrollIntoView({behavior:'smooth'}), 50);
      }, async (err)=>{
        console.warn('Mesaj listener hata:', err);
        if(!fallback){
          fallback = true;
          try {
            const simpleQ = query(collection(db,'dm_messages'), where('conversationId','==', activeId), limit(200));
            unsub = onSnapshot(simpleQ, snap2 => {
              const list = snap2.docs.map(d=> ({ id:d.id, ...(d.data() as any) })) as DirectMessage[];
              list.sort((a,b)=> (a.createdAt || '').localeCompare(b.createdAt || ''));
              setMessages(list);
              setTimeout(()=> bottomRef.current?.scrollIntoView({behavior:'smooth'}), 50);
            });
            const first = await getDocs(simpleQ);
            const listFirst = first.docs.map(d=> ({ id:d.id, ...(d.data() as any) })) as DirectMessage[];
            listFirst.sort((a,b)=> (a.createdAt || '').localeCompare(b.createdAt || ''));
            setMessages(listFirst);
            setMsgError('Index eksik: dm_messages conversationId == & createdAt asc. Performans için oluşturun.');
          } catch(e2:any){
            setMsgError('Mesajlar yüklenemedi: '+(e2?.message||'bilinmeyen hata'));
          }
        } else {
          setMsgError('Mesajlar yüklenemedi');
        }
      });
    } catch(e:any){
      setMsgError(e?.message || 'Mesajlar yüklenemedi');
    }
    return ()=> { if(unsub) unsub(); };
  },[activeId]);

  // Auto start with userId (legacy path)
  useEffect(()=>{ if(startWithUserId && !conversationId) { startDirectWith(startWithUserId); } }, [startWithUserId, startDirectWith, conversationId]);

  // Directly open provided conversationId
  useEffect(()=>{ if(conversationId){ setActiveId(conversationId); } }, [conversationId]);

  if(!isLoggedIn) return null;

  const DESKTOP_WIDTH = 440; // px
  const MAX_PANEL_HEIGHT = '60vh';

  const containerStyle: React.CSSProperties = isMobile ? {
    position:'fixed', inset:0, zIndex:3000, display:'flex', flexDirection:'column', padding:'0.9rem 0.9rem 1.1rem', background:'rgba(0,0,0,0.55)', backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)'
  } : {
    position:'fixed', right:'1rem', bottom:'1rem', width: DESKTOP_WIDTH+'px', maxWidth:'100%', zIndex:3000, display:'flex', flexDirection:'column', gap:'.65rem'
  };

  const shellStyle: React.CSSProperties = isMobile ? {
    display:'flex', flexDirection:'column', gap:'.75rem', flex:1
  } : { display:'flex', flexDirection:'column', gap:'.6rem' };

  const panesWrapper: React.CSSProperties = isMobile ? {
    display:'flex', flexDirection:'column', gap:'.75rem', flex:1, overflow:'hidden'
  } : { display:'flex', gap:'.5rem' };

  const convListStyle: React.CSSProperties = isMobile ? {
    flex:'0 0 auto', background:'var(--glass-background)', border:'var(--glass-border)', backdropFilter:'blur(var(--glass-blur))', WebkitBackdropFilter:'blur(var(--glass-blur))', borderRadius:'22px', padding:'.75rem', display:'flex', flexDirection:'row', gap:'.5rem', overflowX:'auto', maxWidth:'100%'
  } : { flex:'0 0 165px', background:'var(--glass-background)', border:'var(--glass-border)', backdropFilter:'blur(var(--glass-blur))', WebkitBackdropFilter:'blur(var(--glass-blur))', borderRadius:'18px', padding:'.55rem', display:'flex', flexDirection:'column', gap:'.45rem', maxHeight: MAX_PANEL_HEIGHT, overflowY:'auto' };

  const convBtnBase: React.CSSProperties = isMobile ? {
    minWidth:'120px', textAlign:'left', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.18)', borderRadius:'16px', padding:'.45rem .55rem', cursor:'pointer'
  } : { textAlign:'left', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.18)', borderRadius:'14px', padding:'.4rem .5rem', cursor:'pointer' };

  const activeConvStyle: React.CSSProperties = { background:'rgba(255,255,255,.15)' };

  const messagesPaneStyle: React.CSSProperties = isMobile ? {
    flex:1, background:'var(--glass-background)', border:'var(--glass-border)', backdropFilter:'blur(var(--glass-blur))', WebkitBackdropFilter:'blur(var(--glass-blur))', borderRadius:'22px', padding:'.75rem', display:'flex', flexDirection:'column', gap:'.55rem', minHeight:0
  } : { flex:1, background:'var(--glass-background)', border:'var(--glass-border)', backdropFilter:'blur(var(--glass-blur))', WebkitBackdropFilter:'blur(var(--glass-blur))', borderRadius:'18px', padding:'.65rem', display:'flex', flexDirection:'column', gap:'.55rem', maxHeight: MAX_PANEL_HEIGHT };

  return (
    <div style={containerStyle}>
      <div style={shellStyle}>
        <div style={{background:'var(--glass-background)', border:'var(--glass-border)', backdropFilter:'blur(var(--glass-blur))', WebkitBackdropFilter:'blur(var(--glass-blur))', borderRadius: isMobile? '24px':'18px', padding: isMobile? '.75rem .9rem':'.6rem .75rem', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div style={{fontSize:'.75rem', fontWeight:600}}>Mesajlar</div>
          <div style={{display:'flex', gap:'.45rem'}}>
            <button style={{fontSize:'.55rem', background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.2)', padding:'.35rem .7rem', borderRadius:'14px', cursor:'pointer'}} onClick={loadConversations}>Yenile</button>
            {onClose && <button style={{fontSize:'.55rem', background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.2)', padding:'.35rem .7rem', borderRadius:'14px', cursor:'pointer'}} onClick={onClose}>{isMobile? 'Kapat':'Kapat'}</button>}
          </div>
        </div>
        <div style={panesWrapper}>
          <div style={convListStyle}>
            {loadingConv && <div style={{fontSize:'.55rem', opacity:.7}}>Yükleniyor...</div>}
            {convError && <div style={{fontSize:'.5rem', color:'#f88'}}>{convError}</div>}
            {conversations.map(c => {
              const otherId = c.participants.find(p=> p!==userProfile!.uid)!;
              const prof = c.participantProfiles?.[otherId];
              const active = c.id===activeId;
              return (
                <button key={c.id} onClick={()=>openConversation(c.id)} style={{...convBtnBase, ...(active? activeConvStyle:{})}}>
                  <div style={{fontSize:'.55rem', fontWeight:600}}>{prof?.displayName || 'Kullanıcı'}</div>
                  {c.lastMessage && !isMobile && <div style={{fontSize:'.5rem', opacity:.65, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{c.lastMessage}</div>}
                </button>
              );
            })}
            {conversations.length===0 && !loadingConv && <div style={{fontSize:'.55rem', opacity:.6}}>Konuşma yok</div>}
          </div>
          <div style={messagesPaneStyle}>
            {activeId ? (
              <>
                <div style={{flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:'.5rem', paddingRight:'.25rem', minHeight:0}}>
                  {loadingMsgs && <div style={{fontSize:'.55rem', opacity:.7}}>Mesajlar yükleniyor...</div>}
                  {msgError && <div style={{fontSize:'.5rem', color:'#f99'}}>{msgError}</div>}
                  {messages.map(m => {
                    const mine = m.senderId === userProfile!.uid;
                    return (
                      <div key={m.id} style={{alignSelf: mine? 'flex-end':'flex-start', maxWidth:'85%', background: mine? 'linear-gradient(135deg,var(--primary-color),var(--secondary-color))':'rgba(255,255,255,.12)', color:'#fff', border:'1px solid rgba(255,255,255,.25)', padding:'.48rem .6rem', fontSize:'.58rem', borderRadius: mine? '16px 16px 6px 16px':'16px 16px 16px 6px'}}>
                        {m.content}
                        <div style={{fontSize:'.45rem', opacity:.6, marginTop:'.25rem'}}>{new Date(m.createdAt).toLocaleTimeString()}</div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <div style={{display:'flex', gap:'.5rem'}}>
                  <input value={msgInput} onChange={e=>setMsgInput(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); handleSend(); } }} placeholder="Mesaj yaz..." style={{flex:1, background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.2)', borderRadius:'16px', padding:'.55rem .75rem', fontSize:'.6rem', color:'var(--text-color,#fff)'}} />
                  <button onClick={handleSend} disabled={!msgInput.trim()} style={{background:'linear-gradient(135deg,var(--primary-color),var(--secondary-color))', border:'1px solid rgba(var(--primary-color-rgb),0.5)', color:'#fff', fontSize:'.6rem', padding:'.55rem .9rem', borderRadius:'16px', cursor:'pointer'}}>Gönder</button>
                </div>
              </>
            ) : (
              <div style={{fontSize:'.65rem', opacity:.65, display:'flex', alignItems:'center', justifyContent:'center', flex:1}}>Bir konuşma seç.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MessagingPanel;